import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useAddAchCheck } from '@/api/hooks/paymentAccountsHooks';
import { BillingInfoResponse } from '@/api/models/individualModels';
import AddAchForm from '@/components/forms/AddAchForm';
import { ENV } from '@/constants/config';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { AddAchData } from '@/lib/validations/PaymentMethodsSchema';
import { decryptObject, encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

import { TokenizedACHData } from './achList';

const AddAch = () => {
  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const { mutateAsync: addAchCheck, isPending } = useAddAchCheck();

  const { showSuccess } = useEndFlowModal();

  const [payloadState, setPayloadState] = useState<{
    donationData: string;
    fromBillingData: string;
    individualNuveiUser: string;
    fromIOS?: string;
    influencerCode?: string;
    routeWithSelectedMethod?: string;
    subscriptionItem?: string;
    showId?: string;
  }>();

  const [parsedIndividualNuveiUser, setParsedIndividualNuveiUser] = useState<BillingInfoResponse>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        donationData: string;
        fromBillingData: string;
        individualNuveiUser: string;
        fromIOS?: string;
        influencerCode?: string;
        routeWithSelectedMethod?: string;
        subscriptionItem?: string;
        showId?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
        setParsedIndividualNuveiUser(safeJsonParse<BillingInfoResponse>(payloadObject.individualNuveiUser ?? ''));
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const onDone = (data: AddAchData) => {
    addAchCheck(
      {
        check_type: data.personalBusinessType,
        account_type: data.checkingSavingType,
        name_on_check: data.nameOnAccount,
        account_number: data.accountNumber,
        // On Nuvei testing enviroment only this number works, just for testing purposes
        transit_number: ENV !== 'production' ? '490000018' : data.routingNumber
      },
      {
        onSuccess: async (response) => {
          const tokenizedACH: TokenizedACHData = {
            check_key: response.check_key,
            account_number_last_four_digits: data.accountNumber.substring(data.accountNumber.length - 4),
            account_type: data.personalBusinessType,
            name_on_check: data.nameOnAccount,
            check_type: data.checkingSavingType
          };

          showSuccess({ title: i18n.t('end_flow.success_title'), message: i18n.t('payment_methods.ach_added') });

          const newPayload = { ...payloadState, tokenizedACH: JSON.stringify(tokenizedACH) };

          const encrypted = await encryptObject(newPayload);

          // If comes from billing end process
          if (payloadState?.fromBillingData === 'true') {
            if (payloadState?.donationData) {
              router.replace({
                pathname: '/(app)/donationPayment',
                params: {
                  payload: encodeURIComponent(encrypted)
                }
              });
            } else {
              // Check if param is route
              router.replace({
                pathname: payloadState?.routeWithSelectedMethod as any,
                params: {
                  payload: encodeURIComponent(encrypted)
                }
              });
            }
          } else {
            // Go to billing with ACH data to update billing address and end donation
            router.replace({
              pathname: '/(app)/paymentMethods/billingAddress',
              params: {
                payload: encodeURIComponent(encrypted)
              }
            });
          }
        },
        onError: (e) => {
          console.error(e);
        }
      }
    );
  };

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        <AddAchForm
          initialData={{
            checkingSavingType: 'CHECKING',
            personalBusinessType: 'PERSONAL',
            nameOnAccount: parsedIndividualNuveiUser?.customerName ?? '',
            accountNumber: '',
            routingNumber: ''
          }}
          onDone={onDone}
          isLoading={isPending}
        />
      </View>
    </SafeAreaView>
  );
};

export default AddAch;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    flex: 1,
    alignItems: 'center',
    gap: 10
  },

  errorText: {
    fontSize: 16,
    color: 'red'
  }
});
